import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  InputAdornment,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  useTheme
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  SmartToy,
  Code
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/login`,
        formData
      );

      if (!response.data.isEmailVerified) {
        setError('Please verify your email before logging in.');
        setIsSubmitting(false);
        return;
      }
      
      login(response.data.token);
      navigate('/'); // This will redirect to Dashboard after login
    } catch (err) {
      setError(
        err.response?.data?.message ||
        'Login failed. Please check your credentials and try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, #2c3e50 100%)`,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          zIndex: 1
        }}
      >
        {[...Array(6)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              width: ['100px', '150px', '200px'][i % 3],
              height: ['100px', '150px', '200px'][i % 3],
              background: `rgba(76, 133, 240, ${0.03 + (i % 3) * 0.02})`,
              borderRadius: '50%',
              top: `${10 + i * 15}%`,
              left: `${5 + i * 15}%`,
              animation: `float ${5 + i * 2}s infinite ease-in-out alternate`
            }}
          />
        ))}
      </Box>

      <Container maxWidth="lg" sx={{ zIndex: 2 }}>
        <Grid container spacing={0} alignItems="center" justifyContent="center" sx={{ minHeight: '100vh' }}>
          {/* Left side - Hero content */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              pr: 5,
            }}
          >
            <Box 
              component="img"
              src="/robot1.svg" 
              alt="AI Robot"
              sx={{
                width: '70%',
                maxWidth: 400,
                mb: 3,
                filter: 'drop-shadow(0 10px 10px rgba(0,0,0,0.2))',
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNTYgMjU2Ij48cmVjdCB3aWR0aD0iMjU2IiBoZWlnaHQ9IjI1NiIgZmlsbD0ibm9uZSIvPjxjaXJjbGUgY3g9IjEyOCIgY3k9IjEyOCIgcj0iNzAiIGZpbGw9IiM0Qzg1RjAiLz48cmVjdCB4PSI4OCIgeT0iODgiIHdpZHRoPSI4MCIgaGVpZ2h0PSI4MCIgcng9IjE2IiBmaWxsPSIjMjEyMTMxIi8+PHJlY3QgeD0iMTA0IiB5PSIxMDQiIHdpZHRoPSI0OCIgaGVpZ2h0PSI0OCIgcng9IjgiIGZpbGw9IiM0Qzg1RjAiLz48L3N2Zz4=';
              }}
            />
            <Typography 
              variant="h3" 
              component="h1" 
              sx={{ 
                color: 'white',
                fontWeight: 700,
                textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                mb: 2
              }}
            >
              GorillaForge AI
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'white',
                opacity: 0.85,
                textAlign: 'center',
                maxWidth: '80%',
                textShadow: '0 1px 5px rgba(0,0,0,0.2)'
              }}
            >
              Build intelligent bots. Unleash AI strength.
              The primal force behind smart automation.
            </Typography>
          </Grid>

          {/* Right side - Login form */}
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={10}
              sx={{
                p: 4,
                borderRadius: 3,
                backdropFilter: 'blur(10px)',
                background: 'rgba(255, 255, 255, 0.9)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
              }}
            >
              <Box display="flex" alignItems="center" mb={3}>
                <SmartToy 
                  sx={{ 
                    color: theme.palette.primary.main, 
                    fontSize: '2rem',
                    mr: 1 
                  }} 
                />
                <Typography variant="h4" component="h1" fontWeight="700">
                  Welcome Back
                </Typography>
              </Box>

              <Typography variant="body1" color="text.secondary" mb={4}>
                Login to access your AI chatbots dashboard
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  sx={{
                    mt: 3,
                    mb: 2,
                    py: 1.2,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                      transform: 'translateX(-100%)',
                      animation: 'shimmer 1.5s infinite'
                    }
                  }}
                >
                  {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Login'}
                </Button>
              </form>

              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  OR
                </Typography>
              </Divider>

              <Box mt={2} textAlign="center">
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Don't have an account?
                </Typography>
                <Button
                  component={Link}
                  to="/register"
                  variant="outlined"
                  startIcon={<Code />}
                  fullWidth
                >
                  Create Account
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* CSS for animations */}
      <style jsx global>{`
        @keyframes float {
          0% {
            transform: translateY(0) rotate(0deg);
          }
          100% {
            transform: translateY(-20px) rotate(10deg);
          }
        }
        
        @keyframes shimmer {
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
    </Box>
  );
};

export default Login;
